import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

import { MdbCheckboxModule, MdbSelectModule, MdbModalRef, MdbFormsModule } from 'mdb5-adapted';
import { AppUtilityService } from 'src/app/app-utility.service';
import { BankDetailsModel, BanksModel, QuoteModel } from 'src/app/core/models';
import { QuoteStoreService } from 'src/app/core/rest-services/quote-store.service';
import { UserService } from 'src/app/core/services/lmsuser.service';
import { DisableControlDirective, LMS_NoSpecialsDirective } from 'src/app/shared/directives';

import { LMS_NoPasteDirective, LmsModalFrameComponent, NumbersOnlyDirective } from '@lmsSharedUI';

@Component({
  selector: 'app-banking-details',
  templateUrl: './banking-details.component.html',
  styleUrls: ['./banking-details.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    LmsModalFrameComponent,
    FormsModule,
    ReactiveFormsModule,
    MdbCheckboxModule,
    DisableControlDirective,
    LMS_NoPasteDirective,
    LMS_NoSpecialsDirective,
    NumbersOnlyDirective,
    MdbSelectModule,
    MdbFormsModule
  ]
})
export class BankingDetailsComponent implements OnInit {
  //@ViewChild('sameAccountCheck',{ static: true }) sameAccountCheck: ElementRef<HTMLInputElement>;

  public agreeControl = new FormControl(false); // default unchecked

  clientDetails: any;
  bankingDetails: any;
  disburseAccount: any;
  collectAccount: any;
  hasAccount = false;
  isEditing = false;

  bankDetailsModel = {} as BankDetailsModel;
  allBanks: BanksModel[] = [];
  debiCheckBanks: BanksModel[] = [];

  isBankingLoading = false;

  bankDetailsForm!: FormGroup;
  indeterminate = false;
  labelPosition: 'before' | 'after' = 'after';
  submitted = false;
  quoteModel: QuoteModel;

  isUserNotBranchConsultant =
    this.userService.userJobTitle.jobTitle !== 'Branch Consultant';
  isUserBranchManager =
    this.userService.userJobTitle.jobTitle === 'Branch Manager' ||
    this.userService.userJobTitle.jobTitle === 'System Owner';
  isReadOnly = false;
  clientStatus: string;

  constructor(
    public modalRef: MdbModalRef<BankingDetailsComponent>,
    public quoteStoreService: QuoteStoreService,
    private userService: UserService,
    private fb: FormBuilder,
    public global_utility: AppUtilityService,
    private cdr: ChangeDetectorRef,
  ) {
    this.bankDetailsForm = this.fb.group({
      accountNumber: ['', Validators.required],
      accountType: ['', Validators.required],
      accountHolder: ['', Validators.required],
      accountLoanType: [''],
      bank: ['', Validators.required],
      branchCode: [''],
      collect_accountNumber: ['', Validators.required],
      collect_accountType: ['', Validators.required],
      collect_accountHolder: ['', Validators.required],
      collect_bank: ['', Validators.required],
      collect_accountLoanType: [''],
      collect_branchCode: [''],
      payoutMethod: ['', Validators.required],
      sameAsDisburseAccount: [true, []],
    });
  }

  get editAllowed() {
    const isBranchConsultant =
      this.userService.userJobTitle.jobTitle === 'Branch Consultant';
    const isBranchManager =
      this.userService.userJobTitle.jobTitle === 'Branch Manager';
    const isSystemOwner =
      this.userService.userJobTitle.jobTitle === 'System Owner';

    return (
      (isBranchConsultant || isBranchManager || isSystemOwner) && this.isEditing
    );
  }

  get accountNumber() {
    return this.bankDetailsForm.get('accountNumber');
  }
  get accountType() {
    return this.bankDetailsForm.get('accountType');
  }
  get accountHolder() {
    return this.bankDetailsForm.get('accountHolder');
  }
  get accountLoanType() {
    return this.bankDetailsForm.get('accountLoanType');
  }
  get bank() {
    return this.bankDetailsForm.get('bank');
  }
  get branchCode() {
    return this.bankDetailsForm.get('branchCode');
  }
  get collect_accountNumber() {
    return this.bankDetailsForm.get('collect_accountNumber');
  }
  get collect_accountType() {
    return this.bankDetailsForm.get('collect_accountType');
  }
  get collect_accountHolder() {
    return this.bankDetailsForm.get('collect_accountHolder');
  }
  get collect_bank() {
    return this.bankDetailsForm.get('collect_bank');
  }
  get collect_accountLoanType() {
    return this.bankDetailsForm.get('collect_accountLoanType');
  }
  get collect_branchCode() {
    return this.bankDetailsForm.get('collect_branchCode');
  }
  get payoutMethod() {
    return this.bankDetailsForm.get('payoutMethod');
  }
  get sameAsDisburseAccount() {
    return this.bankDetailsForm.get('sameAsDisburseAccount');
  }

  ngOnInit(): void {
    console.log('Banking details init...');
    this.isBankingLoading = true;
    this.clientStatus = this.clientDetails.clientStatus;

    this.validateAccounts();
    if (!this.isReadOnly) {
      this.quoteStoreService
        .getAllBanks().subscribe({
          next:(res) => {
            this.allBanks = res;
            this.cdr.markForCheck();
          }
        });

      this.quoteStoreService
        .getDebiCheckBanks().subscribe({
          next: (res) => {
            this.debiCheckBanks = res;
            this.cdr.markForCheck();
          }
        });
    }

    this.populateDTO_bankingDetailsModel();
    this.populateFormfromModel();

    // Set initial form state
    if (this.hasAccount && !this.isEditing) {
      this.disableAllControls();
    }

    this.sameAsDisburseAccount?.setValue(
      this.bankDetailsModel.sameAsDisburseAccount,
      { onlySelf: false, emitEvent: true },
    );
    this.sameAsDisburseAccount?.valueChanges.subscribe((value) => {
      this.bankDetailsModel.sameAsDisburseAccount = value;
      this.onSameAccountChange();
    });
    if (
      (this.bankDetailsModel?.sameAsDisburseAccount &&
        this.bankDetailsModel?.colBank === 'Finbond Mutual Bank') ||
      (!this.bankDetailsModel?.sameAsDisburseAccount &&
        this.bankDetailsModel?.bank === 'Finbond Mutual Bank')
    ) {
      this.bankDetailsModel.payoutMethod = 'FINBOND CARD';
      this.bankDetailsForm.controls['payoutMethod'].setValue('FINBOND CARD', {
        onlySelf: false,
        emitEvent: true,
      });
    }
    this.cdr.markForCheck();
    this.cdr.detectChanges();

    this.isBankingLoading = false;
  }

  validateAccounts() {
    this.hasAccount = false;

    if (!this.bankingDetails || this.bankingDetails.length == 0) {
      this.disburseAccount = {};
      this.collectAccount = {};
      this.bankDetailsModel.sameAsDisburseAccount = false;
      return;
    }

    if (this.bankingDetails && this.bankingDetails.length > 0) {
      const hasAcc = this.bankingDetails[0];
      if (hasAcc) {
        const hasAccNo = this.bankingDetails[0].accountNumber;
        if (hasAccNo) {
          this.hasAccount = true;
          for (const acc of this.bankingDetails) {
            if (acc.loanType == 'DISBURSE') {
              this.disburseAccount = acc;
            } else if (acc.loanType == 'COLLECT') {
              this.collectAccount = acc;
            }
          }
        }
      }
    }
  }

  private populateDTO_bankingDetailsModel() {
    this.bankDetailsModel.payoutMethod =
      this.quoteStoreService.quoteWorkbook?.payoutMethod ||
      this.clientDetails.payoutMethod;
    this.bankDetailsModel.prevPayoutMethod =
      this.quoteStoreService.quoteWorkbook?.payoutMethod ||
      this.clientDetails.payoutMethod;

    this.bankDetailsModel.quoteId =
      this.quoteStoreService.quoteWorkbook?.id || this.clientDetails.id;

    if (this.disburseAccount && Object.keys(this.disburseAccount).length > 0) {
      this.bankDetailsModel.accountNumber = this.disburseAccount.accountNumber;
      this.bankDetailsModel.accountType = this.disburseAccount.accountType;
      this.bankDetailsModel.accountHolder = this.disburseAccount.accountHolder;
      this.bankDetailsModel.bank = this.disburseAccount.bank;
      this.bankDetailsModel.accountLoanType = this.disburseAccount.loanType;
      this.bankDetailsModel.branchCode = this.disburseAccount.branchCode;
    }

    if (this.collectAccount && Object.keys(this.collectAccount).length > 0) {
      this.bankDetailsModel.colAccountNumber =
        this.collectAccount.accountNumber;
      this.bankDetailsModel.colAccountType = this.collectAccount.accountType;
      this.bankDetailsModel.colAccountHolder =
        this.collectAccount.accountHolder;
      this.bankDetailsModel.colBank = this.collectAccount.bank;
      this.bankDetailsModel.colAccountLoanType = this.collectAccount.loanType;
      this.bankDetailsModel.colBranchCode = this.collectAccount.branchCode;
    }

    this.bankDetailsModel.sameAsDisburseAccount =
      this.collectAccount &&
      this.disburseAccount &&
      Object.keys(this.collectAccount).length > 0 &&
      Object.keys(this.disburseAccount).length > 0 &&
      this.bankDetailsModel.accountNumber ==
        this.bankDetailsModel.colAccountNumber &&
      this.bankDetailsModel.bank == this.bankDetailsModel.colBank &&
      this.bankDetailsModel.branchCode == this.bankDetailsModel.colBranchCode;

    this.cdr.markForCheck();
    this.cdr.detectChanges();
    //this.sameAsDisburseAccount.setValue(this.bankDetailsModel.sameAsDisburseAccount);
    // this.sameAccountCheck.nativeElement.checked = this.bankDetailsModel.sameAsDisburseAccount;
    //this.onSameAccountChange();

    this.updatePAyout();
  }

  private updatePAyout() {
    if (this.bankDetailsModel.sameAsDisburseAccount) {
      if (this.collect_bank?.value == 'Finbond Mutual Bank') {
        this.bankDetailsModel.payoutMethod = 'FINBOND CARD';
      } else {
        this.bankDetailsModel.payoutMethod = 'CBS BANK TRANSFER';
      }
    } else {
      if (this.bank?.value == 'Finbond Mutual Bank') {
        this.bankDetailsModel.payoutMethod = 'FINBOND CARD';
      } else {
        this.bankDetailsModel.payoutMethod = 'CBS BANK TRANSFER';
      }
    }

    this.bankDetailsForm.controls?.payoutMethod?.setValue(
      this.bankDetailsModel.payoutMethod,
      { onlySelf: false, emitEvent: true },
    );
    this.cdr.detectChanges();
    this.cdr.markForCheck();
  }

  private populateFormfromModel(): void {
    this.bankDetailsForm.setValue(
      {
        accountNumber: this.disburseAccount?.accountNumber || '',
        accountType: this.disburseAccount?.accountType || '',
        accountHolder: this.disburseAccount?.accountHolder || '',
        bank: this.disburseAccount?.bank || '',
        branchCode: this.disburseAccount?.branchCode || '',
        collect_accountNumber: this.collectAccount?.accountNumber || '',
        collect_accountType: this.collectAccount?.accountType || '',
        collect_accountHolder: this.collectAccount?.accountHolder || '',
        collect_bank: this.collectAccount?.bank || '',
        collect_branchCode: this.collectAccount?.branchCode || '',
        payoutMethod: this.bankDetailsModel.payoutMethod || '',
        sameAsDisburseAccount:
          this.bankDetailsModel.sameAsDisburseAccount || false,
        accountLoanType: null,
        collect_accountLoanType: null,
      },
      { onlySelf: false, emitEvent: true },
    );

    this.payoutMethod?.setValue(this.bankDetailsModel.payoutMethod);
  }

  checkEmidClient() {
    if (
      this.clientDetails.clientStatus == 'EMID' &&
      this.bankingDetails.bankAccounts.length == 0
    ) {
      this.quoteModel = new QuoteModel();
      this.quoteModel.clientNumber = this.clientDetails.clientNumber;
      this.quoteStoreService
        .bankingDetailsFromEmid(this.quoteModel)
        .subscribe((res) => {
          console.log(res);
        });
    }
  }

  private FormValuestoDTO(frmFields: any) {
    this.bankDetailsModel.accountNumber =
      frmFields.accountNumber || this.bankDetailsModel.accountNumber;
    this.bankDetailsModel.accountType =
      frmFields.accountType || this.bankDetailsModel.accountType;
    this.bankDetailsModel.accountHolder =
      frmFields.accountHolder || this.bankDetailsModel.accountHolder;
    this.bankDetailsModel.bank = frmFields.bank || this.bankDetailsModel.bank;
    this.bankDetailsModel.branchCode =
      frmFields.branchCode || this.bankDetailsModel.branchCode;

    this.bankDetailsModel.colAccountNumber =
      frmFields.collect_accountNumber || this.bankDetailsModel.colAccountNumber;
    this.bankDetailsModel.colAccountType =
      frmFields.collect_accountType || this.bankDetailsModel.colAccountType;
    this.bankDetailsModel.colAccountHolder =
      frmFields.collect_accountHolder || this.bankDetailsModel.colAccountHolder;
    this.bankDetailsModel.colBank =
      frmFields.collect_bank || this.bankDetailsModel.colBank;
    this.bankDetailsModel.colBranchCode =
      frmFields.collect_branchCode || this.bankDetailsModel.colBranchCode;

    this.bankDetailsModel.payoutMethod =
      frmFields.payoutMethod || this.bankDetailsModel.payoutMethod;
    this.bankDetailsModel.sameAsDisburseAccount =
      frmFields.sameAsDisburseAccount ||
      this.bankDetailsModel.sameAsDisburseAccount;
  }

  close() {
    this.modalRef.close();
  }

  onSameAccountChange(event?: Event) {
    //let  isSameAccount =   false;

    console.log(
      'bankingdetails Model - bankDetailsModel.sameAsDisburseAccount',
      this.bankDetailsModel.sameAsDisburseAccount,
    );

    const isSameAccount = this.sameAsDisburseAccount?.value;
    this.bankDetailsModel.sameAsDisburseAccount = isSameAccount;
    ///this.sameAccountCheck.nativeElement.checked = this.sameAsDisburseAccount.value;

    if (isSameAccount) {
      this.collect_accountNumber?.setValue(this.collect_accountNumber?.value);
      this.collect_accountType?.setValue(this.collect_accountType?.value);
      this.collect_accountHolder?.setValue(this.collect_accountHolder?.value);
      this.collect_branchCode?.setValue(this.collect_branchCode?.value);
      this.collect_bank?.setValue(this.collect_bank?.value);

      this.accountNumber?.disable();
      this.accountType?.disable();
      this.accountHolder?.disable();
      this.bank?.disable();
      this.branchCode?.disable();
      if (this.bank?.value != this.collect_bank?.value) {
        this.bank?.setValue(
          this.allBanks.find((b) => b.bankName === this.bankDetailsModel.bank),
        );
      }
    } else {
      this.collect_accountNumber?.enable();
      this.collect_accountType?.enable();
      this.collect_accountHolder?.enable();
      this.collect_bank?.enable();
      this.collect_branchCode?.enable();

      this.accountNumber?.enable();
      this.accountType?.enable();
      this.accountHolder?.enable();
      this.bank?.enable();
      this.branchCode?.enable();
    }

    const debicheckacc = this.debiCheckBanks.find(
      (b) => b.bankName === this.bankDetailsModel.colBank,
    );
    this.collect_bank?.setValue(
      isSameAccount ? debicheckacc?.bankName : this.collect_bank?.value,
    );

    if (this.bank?.value != this.collect_bank?.value && isSameAccount) {
      this.bank?.setValue(
        this.allBanks.find(
          (b) =>
            b.bankName ===
            (isSameAccount
              ? debicheckacc?.bankName
              : this.bankDetailsModel.bank),
        ),
      );
    }

    this.updatePAyout();
    console.log(
      'bankingdetails Model - bankDetailsModel.payoutMethod',
      this.bankDetailsModel.payoutMethod,
    );
  }

  onBankSelect(event: any) {
    const selbank = this.allBanks.find((b) => b.bankName === event.value);
    if (selbank) {
      this.bankDetailsModel.branchCode = selbank.branchCode!;
      this.bankDetailsModel.bank = selbank.bankName!;
      this.bank?.setValue(selbank.bankName);
      this.branchCode?.setValue(selbank.branchCode);

      if (this.bankDetailsModel.sameAsDisburseAccount) {
        this.bankDetailsModel.colBank = selbank.bankName!;
        this.bankDetailsModel.colBranchCode = selbank.branchCode!;
        this.collect_bank?.setValue(selbank.bankName);
        this.collect_branchCode?.setValue(selbank.branchCode);
      }
    }
    if (!this.bankDetailsModel.sameAsDisburseAccount) {
      this.updatePAyout();
    }
  }

  onColBankSelect(event: any) {
    const selbank = this.debiCheckBanks.find((b) => b.bankName === event.value);
    if (selbank) {
      this.bankDetailsModel.colBank = selbank.bankName!;
      this.bankDetailsModel.colBranchCode = selbank.branchCode!;
      this.collect_branchCode?.setValue(selbank.branchCode);
      this.collect_bank?.setValue(selbank.bankName);
    }

    if (this.bankDetailsModel.sameAsDisburseAccount) {
      this.updatePAyout();
    }
  }

  editBankDetails(): void {
    if (this.isReadOnly) {
      this.global_utility.warning(
        'Editing restricted in Read-only view',
        'Read-only View',
      );
      return;
    }

    if (this.isEditing) {
      this.saveBankDetails();
    } else {
      this.isEditing = true;
      this.enableEditingControls();
      this.cdr.detectChanges();
      this.cdr.markForCheck();

      if (this.bankDetailsModel.sameAsDisburseAccount) {
        // this.accountNumber?.disable();
        this.accountType?.disable();
        this.accountHolder?.disable();
        this.bank?.disable();
        this.branchCode?.disable();
      }

      this.updatePAyout();
    }
  }

  saveBankDetails() {
    this.submitted = true;

    if (this.bankDetailsForm.invalid) {
      this.global_utility.warning(
        'Please enter all the banking details needed',
        'Banking details',
      );
      return;
    }

    this.FormValuestoDTO(this.bankDetailsForm.value);
    this.isEditing = false;
    this.disableAllControls();
    return this.modalRef.close(this.bankDetailsModel);
  }

  private enableEditingControls(): void {
    // this.accountNumber?.enable();
    // this.accountType?.enable();
    // this.accountHolder?.enable();
    // this.bank?.enable();
    //  this.branchCode?.enable();
    //  this.payoutMethod?.enable();

    this.accountNumber?.disable();
    this.accountType?.disable();
    this.accountHolder?.disable();
    this.bank?.disable();
    this.branchCode?.disable();

    this.payoutMethod?.enable();

    this.collect_accountNumber?.enable();
    this.collect_accountType?.enable();
    this.collect_accountHolder?.enable();
    this.collect_bank?.enable();
    this.collect_branchCode?.enable();

    this.sameAsDisburseAccount?.enable();

    const debicheckacc = this.debiCheckBanks.find(
      (b) => b.bankName === this.bankDetailsModel.colBank,
    );
    // if (this.bankDetailsModel.sameAsDisburseAccount && !debicheckacc) {
    //   this.collect_bank?.setValue('');
    //   this.collect_bank?.reset();
    //   this.global_utility.warning('The selected Disbursement bank is not a DebiCheck bank. Please select a valid DebiCheck bank for the Collection account.', 'Invalid DebiCheck Bank');
    // }

    if (!this.bankDetailsModel.sameAsDisburseAccount) {
      this.collect_accountNumber?.enable();
      this.collect_accountType?.enable();
      this.collect_accountHolder?.enable();
      this.collect_bank?.enable();
      this.collect_branchCode?.enable();
      this.collect_bank?.enable();
      this.accountNumber?.enable();
      this.accountType?.enable();
      this.accountHolder?.enable();
      this.bank?.enable();
      this.branchCode?.enable();
      this.bank?.enable();

      this.payoutMethod?.enable();
    }
  }

  private disableAllControls(): void {
    this.accountNumber?.disable();
    this.accountType?.disable();
    this.accountHolder?.disable();
    this.bank?.disable();
    this.branchCode?.disable();
    this.collect_accountNumber?.disable();
    this.collect_accountType?.disable();
    this.collect_accountHolder?.disable();
    this.collect_bank?.disable();
    this.collect_branchCode?.disable();
    this.payoutMethod?.disable();
    //this.sameAsDisburseAccount?.disable();
  }

  confirmBankDetailsDetails() {
    if (this.isReadOnly) {
      this.global_utility.warning(
        'Editing restricted in Read-only view',
        'Read-only View',
      );
      return;
    }

    if (this.payoutMethod?.invalid) {
      this.global_utility.warning(
        'Please confirm if the correct Payout method is selected!',
        'Payout method required',
      );
      return;
    }

    this.bankDetailsModel.payoutMethod = this.payoutMethod?.value || '';
    this.isEditing = false;
    this.disableAllControls();
    this.modalRef.close(this.bankDetailsModel);
  }
}
